import { useSelector } from 'react-redux';
import { MainAppReducer } from 'Types';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';

export interface TierOpenInputs {
  tier: number;
  highestTierComplete: number | null | undefined;
  graded: boolean;
  nodeEnv: string | undefined;
}

/**
 * Whether a sight reading tier can be chosen. Graded play opens tier 1 and the
 * tier after the highest one completed (every tier in local and dev builds).
 * Ungraded play opens every tier: nothing is completed there, so nothing could
 * ever open the next one.
 */
export const isTierOpen = ({
  tier,
  highestTierComplete,
  graded,
  nodeEnv,
}: TierOpenInputs): boolean =>
  !graded ||
  nodeEnv === 'local' ||
  nodeEnv === 'dev' ||
  tier === 1 ||
  (!!highestTierComplete && tier === highestTierComplete + 1);

/**
 * The level repertoire is opened up to. Graded play opens pieces up to the
 * highest level completed; ungraded play opens every piece, up to the top level
 * of the curriculum.
 */
export const openRepertoireUpToLevel = (
  highestLevelCompleted: number,
  graded: boolean,
  curriculumLevelNumbers: readonly number[],
): number =>
  graded ? highestLevelCompleted : Math.max(highestLevelCompleted, ...curriculumLevelNumbers);

const selectCurriculumLevelNumbers = (state: MainAppReducer): number[] =>
  (state.mainAppReducer.levelData ?? []).map((level) => level.level_number);

/** The repertoire level the player can play up to, for Browse and Home. */
export function useOpenRepertoireUpToLevel(highestLevelCompleted: number): number {
  const graded = useEffectiveGrading();
  const curriculumLevelNumbers = useSelector(selectCurriculumLevelNumbers, shallowEqualNumbers);
  return openRepertoireUpToLevel(highestLevelCompleted, graded, curriculumLevelNumbers);
}

const shallowEqualNumbers = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);
